import { COUNTRY } from "@/shared/constants"
import { SuiteCountry } from "@/shared/country/country-scope"

/**
 * Centros (sedes) de operación. Misma tabla que el back (LOGISTICS_CENTERS): un repartidor trabaja en UN centro y solo se le
 * asignan puntos de ese centro (a un punto de Lima no se le asigna un repartidor de Trujillo, ni uno de Quito a uno de Guayaquil).
 * Un repartidor sin centro cargado, o un punto del que no se sabe el centro, no se restringe.
 */
export const LOGISTICS_CENTERS: Record<string, { country: SuiteCountry; nombre: string; aliases: string[] }> = {
  LIM: { country: COUNTRY.PERU, nombre: "Lima", aliases: ["LIM", "LIMA"] },
  TRU: { country: COUNTRY.PERU, nombre: "Trujillo", aliases: ["TRU", "TRUJILLO"] },
  CHI: { country: COUNTRY.PERU, nombre: "Chiclayo", aliases: ["CHI", "CHICLAYO"] },
  AQP: { country: COUNTRY.PERU, nombre: "Arequipa", aliases: ["AQP", "AREQUIPA"] },
  CUS: { country: COUNTRY.PERU, nombre: "Cusco", aliases: ["CUS", "CUSCO"] },
  PIU: { country: COUNTRY.PERU, nombre: "Piura", aliases: ["PIU", "PIURA"] },
  UIO: { country: COUNTRY.ECUADOR, nombre: "Quito", aliases: ["UIO"] },
  GYE: { country: COUNTRY.ECUADOR, nombre: "Guayaquil", aliases: ["GYE"] },
  CUE: { country: COUNTRY.ECUADOR, nombre: "Cuenca", aliases: ["CUE"] },
  AMB: { country: COUNTRY.ECUADOR, nombre: "Ambato", aliases: ["AMB"] },
  LOJ: { country: COUNTRY.ECUADOR, nombre: "Loja", aliases: ["LOJ"] },
  MTA: { country: COUNTRY.ECUADOR, nombre: "Manta", aliases: ["MTA"] },
  MCH: { country: COUNTRY.ECUADOR, nombre: "Machala", aliases: ["MCH"] }
}

/** Opciones del selector de centro de un repartidor, de un país */
export const centerOptions = (country: SuiteCountry): { value: string; label: string }[] =>
  Object.entries(LOGISTICS_CENTERS)
    .filter(([, center]) => center.country === country)
    .map(([value, center]) => ({ value, label: center.nombre }))

/** Nombre legible de un centro ("Trujillo"); si no se conoce, el código tal cual */
export const centerLabel = (code?: string | null): string => (code ? (LOGISTICS_CENTERS[code.trim().toUpperCase()]?.nombre ?? code) : "")

const normalize = (text?: string | null): string =>
  (text ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toUpperCase()

/** Código de centro de un valor suelto ("tru", "Trujillo", "LIMP" = almacén de paquetes). `null` si no es un centro conocido */
export const centerCodeFromText = (text?: string | null): string | null => {
  const value = normalize(text)
  if (!value) return null
  for (const [code, center] of Object.entries(LOGISTICS_CENTERS)) {
    if (center.aliases.includes(value) || (value.length > 1 && value.endsWith("P") && center.aliases.includes(value.slice(0, -1)))) return code
  }
  return null
}

/** Centro que sale del nombre de un albarán: "TRU/Clinica/00001" => "TRU"; "LIMP/LIMAP/Clinica//00013" => "LIM" */
export const centerCodeFromPickingName = (pickingName?: string | null): string | null => {
  for (const segment of (pickingName ?? "").split("/")) {
    const code = centerCodeFromText(segment)
    if (code) return code
  }
  return null
}

export type StopWithPickings = { country?: string | null; center?: string | null; items?: { productos?: { pickingName?: string; paqueteItems?: { pickingName?: string }[] }[] }[] }

/** Marca de un punto de Perú con albaranes pero sin centro reconocible (nombre raro): ningún repartidor con centro lo toma (igual que el back) */
export const UNRECOGNIZED_CENTER = "?"

/**
 * Centro de un punto por los albaranes de sus ítems (el primero que se reconozca). `null` si no se puede saber (punto manual, sin
 * albaranes): ahí no se restringe. Un punto de Perú con albaranes que no se reconocen devuelve UNRECOGNIZED_CENTER.
 */
export const stopCenterOf = (stop?: StopWithPickings | null): string | null => {
  // El centro elegido a mano en el punto manda sobre el de los albaranes
  const chosen = centerCodeFromText(stop?.center)
  if (chosen) return chosen
  let hasPicking = false
  for (const item of stop?.items ?? []) {
    for (const producto of item.productos ?? []) {
      if (producto.pickingName || producto.paqueteItems?.some((p) => p.pickingName)) hasPicking = true
      const code = centerCodeFromPickingName(producto.pickingName) ?? producto.paqueteItems?.map((p) => centerCodeFromPickingName(p.pickingName)).find(Boolean) ?? null
      if (code) return code
    }
  }
  const isEcuador = (stop?.country ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toLowerCase() === "ecuador"
  return hasPicking && !isEcuador ? UNRECOGNIZED_CENTER : null
}

/** ¿Un repartidor de `riderCenter` puede tomar un punto de `stopCenter`? Se rechaza si se conocen los dos y son distintos, o si el punto es UNRECOGNIZED_CENTER y el repartidor tiene centro */
export const isRiderCenterCompatible = (riderCenter?: string | null, stopCenter?: string | null): boolean => {
  const rider = centerCodeFromText(riderCenter)
  if (!rider) return true
  if (stopCenter === UNRECOGNIZED_CENTER) return false
  const stop = centerCodeFromText(stopCenter)
  return !stop || rider === stop
}
